import { notificationApi } from '../../api/notification';
import { useError } from '../../contexts/ErrorContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  FiEye,
  FiUserX,
  FiLock,
  FiLogIn,
  FiLogOut,
  FiBell,
  FiFileText,
  FiMessageCircle,
  FiHeart,
  FiGlobe,
  FiShield,
  FiChevronRight,
  FiInstagram,
  FiVolume2,
} from 'react-icons/fi';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { webPreviewPlayer } from '../preview/previewPlayer';
import PageLayout from '../../components/layout/PageLayout';
import {
  AccountBoundary,
  useSettings,
  useRequestScope,
  SettingsHeader,
  Confirm,
  unwrap,
} from './shared';
import { SettingsFooterLogo } from './SettingsFooterLogo';
export default function SettingsPage() {
  return <AccountBoundary component={Settings} />;
}
function Settings({ user }) {
  const { showError } = useError();
  const { t, dark, locale, setLocale, style } = useSettings(),
    navigate = useNavigate(),
    client = useQueryClient(),
    request = useRequestScope(user);
  const [unread, setUnread] = useState(0),
    [dialog, setDialog] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const lock = useRef(false);
  useEffect(() => {
    if (!user) return;
    const r = request();
    notificationApi
      .getNotifications(r.options)
      .then((response) => {
        if (r.current()) setUnread(unwrap(response).filter((n) => !n.isRead).length);
      })
      .catch(() => {})
      .finally(r.finish);
    return r.cancel;
    // The boundary remounts on account/session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const palette = user?.profileMark?.nicknameColor;
  const nicknameColor =
    palette?.key === 'VINYL' ? (dark ? '#FF79AD' : '#B8326A') : palette?.[dark ? 'dark' : 'light'];
  const clearSession = () => {
    useNotificationStore.getState().disconnectSse();
    useNotificationStore.getState().resetUnreadCount();
    webPreviewPlayer.dismiss();
    useUserStore.getState().clearUser();
    client.clear();
    navigate('/mypage', { replace: true });
  };
  const act = async () => {
    if (lock.current || !user) return;
    if (dialog === 'withdraw') {
      setDialog('withdraw-final');
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    const r = request();
    try {
      if (dialog === 'withdraw-final') await api.putAuth('/me/withdraw', undefined, r.options);
      if (r.current()) {
        clearSession();
        if (dialog === 'withdraw-final')
          showError(
            t('settings.withdrawCompleteTitle') + '\n' + t('settings.withdrawCompleteBody'),
            'success',
          );
      } else if (r.mounted()) setError(t('common.retryLater'));
    } catch {
      if (r.current()) setError(t('settings.withdrawFailed'));
    } finally {
      lock.current = false;
      if (r.mounted()) {
        setBusy(false);
        setDialog(null);
      }
      r.finish();
    }
  };
  const row = (Icon, label, to, detail, action, destructive = false) => {
    const children = (
      <>
        <span className="pp-settings-row-icon">
          <Icon size={19} aria-hidden="true" />
        </span>
        <span className="pp-settings-row-copy">
          <span>{label}</span>
          {detail && <small>{detail}</small>}
        </span>
        {!destructive && <FiChevronRight size={17} aria-hidden="true" />}
      </>
    );
    return to ? (
      <Link className="pp-settings-row" to={to}>
        {children}
      </Link>
    ) : (
      <button
        type="button"
        className={`pp-settings-row${destructive ? ' is-destructive' : ''}`}
        disabled={busy}
        onClick={action}
      >
        {children}
      </button>
    );
  };
  const group = (title, children) => (
    <section className="pp-settings-group">
      <h2>{title}</h2>
      <div>{children}</div>
    </section>
  );
  return (
    <PageLayout paddingTop={0}>
      <div className="pp-settings" style={style}>
        <SettingsHeader title={t('settings.title')} busy={busy} />
        {user ? (
          <>
            <div className="pp-settings-account">
              <strong
                style={{
                  color: /^#[\da-f]{3,8}$/i.test(nicknameColor || '') ? nicknameColor : undefined,
                }}
              >
                {user.nickname}
              </strong>
              <span>{user.email}</span>
            </div>
            {group(
              t('settings.profileAndAccount'),
              <>
                {row(FiEye, t('profileVisibility.title'), '/mypage/visibility')}
                {row(FiUserX, t('safety.blockedUsers'), '/mypage/blocked')}
                {!user.provider && row(FiLock, t('settings.changePassword'), '/password-change')}
              </>,
            )}
          </>
        ) : (
          group(
            t('settings.account'),
            row(FiLogIn, t('settings.loginOrSignup'), '/login?redirectUrl=%2Fmypage%2Fsettings'),
          )
        )}
        {group(
          t('settings.notifications'),
          <>
            {user &&
              row(
                FiBell,
                t('settings.notifications'),
                '/notifications',
                unread ? t('settings.unreadCount', { count: unread }) : null,
              )}
            {row(FiVolume2, t('settings.pushSettings'), '/mypage/push-settings')}
          </>,
        )}
        {group(
          t('settings.guideAndSupport'),
          <>
            {row(FiFileText, t('settings.postpopFeed'), '/feed')}
            {row(FiVolume2, t('settings.noticesAndEvents'), '/notice')}
            {row(FiHeart, t('settings.supportPostpop'), '/sponsorship')}
            {row(FiMessageCircle, t('settings.contact'), '/contact')}
          </>,
        )}
        {group(
          t('settings.appSettings'),
          row(
            FiGlobe,
            t('settings.language'),
            null,
            t(locale === 'ko' ? 'settings.languageKorean' : 'settings.languageEnglish'),
            () => setDialog('language'),
          ),
        )}
        {user &&
          group(
            t('settings.management'),
            <>
              {user.role === 'ADMIN' && row(FiShield, t('settings.admin'), '/admin')}
              {row(FiLogOut, t('settings.logout'), null, null, () => setDialog('logout'), true)}
            </>,
          )}
        {error && <p role="alert">{error}</p>}
        <footer className="pp-settings-footer">
          <SettingsFooterLogo isDark={dark} />
          <div className="pp-settings-footer-links">
            <div className="pp-settings-footer-policy">
              <Link to="/about/terms-of-use">{t('settings.terms')}</Link>
              <span className="pp-settings-footer-dot" aria-hidden="true" />
              <Link to="/about/privacy-policy">{t('settings.privacy')}</Link>
            </div>
            <span
              className="pp-settings-footer-dot pp-settings-footer-group-dot"
              aria-hidden="true"
            />
            <div className="pp-settings-footer-social">
              <a
                href="https://www.instagram.com/postpop.kr"
                target="_blank"
                rel="noreferrer"
                aria-label={t('settings.instagramAccessibility')}
              >
                <FiInstagram size={18} />
              </a>
              <span className="pp-settings-footer-dot" aria-hidden="true" />
              <a
                href="https://www.youtube.com/@포스트팝"
                target="_blank"
                rel="noreferrer"
                aria-label={t('settings.youtubeAccessibility')}
              >
                <svg aria-hidden="true" width="18" height={(18 * 15) / 22} viewBox="1 4.5 22 15">
                  <rect fill="currentColor" height="15" rx="4.5" width="22" x="1" y="4.5" />
                  <path d="M10 8.2L16.2 12 10 15.8Z" fill="var(--settings-background)" />
                </svg>
              </a>
            </div>
          </div>
          <small>© 포스트팝(postpop) All rights reserved.</small>
          {user && (
            <button type="button" disabled={busy} onClick={() => setDialog('withdraw')}>
              {t(busy ? 'settings.withdrawPending' : 'settings.withdraw')}
            </button>
          )}
        </footer>
        {['logout', 'withdraw', 'withdraw-final'].includes(dialog) && (
          <Confirm
            title={t(
              dialog === 'logout'
                ? 'settings.logout'
                : dialog === 'withdraw'
                  ? 'settings.withdraw'
                  : 'settings.finalConfirmation',
            )}
            body={t(
              dialog === 'logout'
                ? 'settings.logoutQuestion'
                : dialog === 'withdraw'
                  ? 'settings.withdrawQuestion'
                  : 'settings.withdrawFinalQuestion',
            )}
            cancel={t('common.cancel')}
            confirm={t(
              dialog === 'logout'
                ? 'settings.logout'
                : dialog === 'withdraw'
                  ? 'common.continue'
                  : 'settings.withdrawAction',
            )}
            busy={busy}
            onCancel={() => setDialog(null)}
            onConfirm={act}
          />
        )}
        {dialog === 'language' && (
          <Dialog
            open
            className="pp-review-dialog"
            fullWidth
            maxWidth="xs"
            onClose={() => setDialog(null)}
            aria-labelledby="settings-language-title"
          >
            <DialogTitle id="settings-language-title">{t('settings.language')}</DialogTitle>
            <DialogContent>
              <p>{t('settings.languageDescription')}</p>
              <div className="pp-settings-language">
                {['ko', 'en'].map((value) => (
                  <Button
                    key={value}
                    aria-pressed={locale === value}
                    onClick={() => {
                      setLocale(value);
                      setDialog(null);
                    }}
                  >
                    {locale === value ? '✓ ' : ''}
                    {t(value === 'ko' ? 'settings.languageKorean' : 'settings.languageEnglish')}
                  </Button>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={() => setDialog(null)}>
                {t('common.cancel')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </div>
    </PageLayout>
  );
}
